import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2 } from "lucide-react";
import { format, parseISO, subDays } from "date-fns";
import {
  getMediaInsights,
  MediaInsightsBucket,
  MediaInsightsDateUnit,
} from "../../api/ProjectApi";
import { formatNumber } from "../../api/DataTypeUtils";

type MetricKey =
  | "postCount"
  | "publishedCount"
  | "totalLikeCount"
  | "totalCommentCount"
  | "totalShareCount"
  | "totalViewCount";

const METRIC_OPTIONS: { key: MetricKey; labelKey: string }[] = [
  { key: "postCount", labelKey: "Posts" },
  { key: "publishedCount", labelKey: "Published" },
  { key: "totalLikeCount", labelKey: "Likes" },
  { key: "totalCommentCount", labelKey: "Comments" },
  { key: "totalShareCount", labelKey: "Shares" },
  { key: "totalViewCount", labelKey: "Views" },
];

const DATE_UNIT_OPTIONS: { value: MediaInsightsDateUnit; labelKey: string }[] = [
  { value: "daily", labelKey: "Daily" },
  { value: "weekly", labelKey: "Weekly" },
  { value: "monthly", labelKey: "Monthly" },
  { value: "quarterly", labelKey: "Quarterly" },
];

const toIsoDate = (d: Date) => format(d, "yyyy-MM-dd");

const defaultTo = () => toIsoDate(new Date());
const defaultFrom = () => toIsoDate(subDays(new Date(), 29));

const formatPeriodLabel = (date: string, dateUnit: MediaInsightsDateUnit) => {
  try {
    const d = parseISO(date);
    switch (dateUnit) {
      case "weekly":
        return format(d, "MMM d");
      case "monthly":
        return format(d, "MMM yyyy");
      case "quarterly": {
        const q = Math.floor(d.getMonth() / 3) + 1;
        return `Q${q} ${format(d, "yyyy")}`;
      }
      default:
        return format(d, "MMM d");
    }
  } catch {
    return date;
  }
};

const formatPeriodTooltip = (date: string, dateUnit: MediaInsightsDateUnit) => {
  try {
    const d = parseISO(date);
    switch (dateUnit) {
      case "weekly":
        return format(d, "MMM d, yyyy");
      case "monthly":
        return format(d, "MMMM yyyy");
      case "quarterly": {
        const q = Math.floor(d.getMonth() / 3) + 1;
        return `Q${q} ${format(d, "yyyy")}`;
      }
      default:
        return format(d, "MMM d, yyyy");
    }
  } catch {
    return date;
  }
};

const validateRange = (from: string, to: string): string | null => {
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return "Invalid date";
  }
  if (toDate < fromDate) {
    return "End date must be on or after start date";
  }
  const spanDays =
    Math.floor((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  if (spanDays < 7) return "Range must be at least 7 days";
  if (spanDays > 365) return "Range must not exceed 365 days";
  return null;
};

const MediaInsightsDailyChart: React.FC = () => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<MetricKey>("postCount");
  const [dateUnit, setDateUnit] = useState<MediaInsightsDateUnit>("daily");
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const rangeError = useMemo(() => validateRange(from, to), [from, to]);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["media-insights", dateUnit, from, to],
    queryFn: () => getMediaInsights({ dateUnit, from, to }),
    enabled: !rangeError,
    staleTime: 60_000,
  });

  const chartData = useMemo(() => {
    const buckets: MediaInsightsBucket[] = data?.buckets ?? [];
    const unit = data?.dateUnit ?? dateUnit;
    return buckets.map((bucket) => ({
      date: bucket.date,
      label: formatPeriodLabel(bucket.date, unit),
      postCount: bucket.postCount,
      publishedCount: bucket.publishedCount,
      totalLikeCount: bucket.totalLikeCount,
      totalCommentCount: bucket.totalCommentCount,
      totalShareCount: bucket.totalShareCount,
      totalViewCount: bucket.totalViewCount,
    }));
  }, [data, dateUnit]);

  const activeMetric = METRIC_OPTIONS.find((m) => m.key === metric)!;
  const activeDateUnit = data?.dateUnit ?? dateUnit;
  const hasData = chartData.some((row) => row[metric] > 0);

  return (
    <div className="mb-4 rounded-md border border-gray-200 bg-white">
      {/* Header row: title + date controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3 pb-2 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{t("Insights by publish date")}</h3>
          <p className="text-[11px] text-gray-400 leading-none mt-0.5">
            {t("Totals grouped by publish date for the selected range")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400">{t("From")}</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400">{t("To")}</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-blue-500"
            />
          </div>
          <select
            value={dateUnit}
            onChange={(e) => setDateUnit(e.target.value as MediaInsightsDateUnit)}
            className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:border-blue-500"
          >
            {DATE_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {rangeError && <p className="text-xs text-red-500 px-4 pt-1">{rangeError}</p>}

      {/* Metric tabs — underline style matching TypeCRM nav */}
      <div className="flex items-center gap-0 border-b border-gray-100 px-4">
        {METRIC_OPTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setMetric(option.key)}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
              metric === option.key
                ? "border-[#2563eb] text-[#2563eb]"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>

      <div className="px-4 pb-3 pt-2">

      {rangeError ? null : isLoading || isFetching ? (
        <div className="flex items-center justify-center h-52 text-gray-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-[#2563eb]" />
          <span className="text-sm">{t("Loading chart…")}</span>
        </div>
      ) : isError ? (
        <p className="text-sm text-red-500 text-center py-12">{t("Failed to load chart data")}</p>
      ) : !hasData ? (
        <p className="text-sm text-gray-400 text-center py-12">
          {t("No published posts with media in this range")}
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#6B7280" }}
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6B7280" }}
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
              tickFormatter={(v) => formatNumber(v)}
              width={48}
            />
            <Tooltip
              cursor={{ fill: "rgba(37, 99, 235, 0.06)" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload;
                return (
                  <div className="rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
                    <p className="font-medium text-gray-300 mb-1">
                      {formatPeriodTooltip(row.date, activeDateUnit)}
                    </p>
                    <p>
                      {t(activeMetric.labelKey)}: {formatNumber(payload[0].value as number)}
                    </p>
                  </div>
                );
              }}
            />
            <Bar
              dataKey={metric}
              name={t(activeMetric.labelKey)}
              fill="#2563eb"
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
      </div>
    </div>
  );
};

export default MediaInsightsDailyChart;